import { F } from '@/utils/intl';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'umi';
import ProForm, { ProFormDigit, ProFormText } from '@ant-design/pro-form';
import { Col, Form, Row, Skeleton, Space, Typography } from 'antd';
import InfoCard from '@/components/ProjectDetails/Cards/InfoCard';
import { SnippetsOutlined } from '@ant-design/icons';
import LeadStateFilters from '@/components/Filters/LeadStateFilters';
import LeadAssigneeDropdownFilters from '@/components/Filters/LeadAssigneeDropdownFilters';
import ProductGroupSelect from '@/components/Filters/ProductGroupSelect';
import { FormInstance } from 'antd/lib/form';
import styles from './index.less';
import ExtraFields from '@/components/LeadSource/ManualLead/ExtraFields';
import BranchServiceSelect from '@/components/Location/BranchServiceSelect';
import type { OwnBranchService } from '@/components/LeadSource/ManualLead/ownBranchService';

interface ProjectFormProps {
  form: FormInstance;
  onProjectExtraFieldValuesChange: (values: any) => void;
  // Branch of a prefilled branch_service_id, so the select can resolve its label.
  branchId?: string | number;
  // The user's only service: shown as a fixed value instead of a select.
  fixedBranchService?: OwnBranchService | null;
  fixedBranchServiceLoading?: boolean;
}

const BRANCH_SERVICE_SELECT_PROPS = { variant: 'underlined', allowClear: true } as const;

// Rendered inside the Form.Item so the preselected id is still submitted.
const FixedBranchService: React.FC<{
  service: OwnBranchService;
  onEdit: () => void;
  value?: string;
}> = ({ service, onEdit }) => (
  <Space size="middle" className={styles.fixedBranchService}>
    <Typography.Text>
      {[service.branchName, service.serviceName].filter(Boolean).join(' > ')}
    </Typography.Text>
    <Typography.Link onClick={onEdit}>
      <F id="common.actions.edit" defaultMessage="Edit" />
    </Typography.Link>
  </Space>
);

const ProjectForm: React.FC<ProjectFormProps> = ({
  form,
  onProjectExtraFieldValuesChange,
  branchId,
  fixedBranchService,
  fixedBranchServiceLoading,
}) => {
  const intl = useIntl();
  // Users who may serve other stores can still switch to the full select.
  const [editingBranchService, setEditingBranchService] = useState(false);
  const enableBranchServiceEdit = useCallback(() => setEditingBranchService(true), []);

  return (
    <InfoCard
      title={
        <Space>
          <SnippetsOutlined />
          <F
            id={'pages.lead.project_information'}
            defaultMessage={'Project Information'}
          />
        </Space>
      }
      variant="purple"
      className={styles.infoCardSection}
    >
      <ProForm
        form={form}
        autoComplete="off"
        size="small"
        submitter={false}
        variant="underlined"
      >
        <Row gutter={16}>
          <Col span={24}>
            <ProFormText
              label={intl.formatMessage({
                id: 'common.attribute.title',
                defaultMessage: 'Title',
              })}
              name="title"
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <F id="pages.leadsources.manual.storeAndService" defaultMessage="Store & Service" />
              }
              name="branch_service_id"
            >
              {fixedBranchServiceLoading ? (
                <Skeleton.Input active size="small" block />
              ) : fixedBranchService && !editingBranchService ? (
                <FixedBranchService service={fixedBranchService} onEdit={enableBranchServiceEdit} />
              ) : (
                <BranchServiceSelect
                  branchId={branchId ?? fixedBranchService?.branchId}
                  selectProps={BRANCH_SERVICE_SELECT_PROPS}
                />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <F
                  id={'pages.leadsources.manual.chooseStatus'}
                  defaultMessage={'Choose status'}
                />
              }
              name="lead_state_id"
            >
              <LeadStateFilters
                type="dropdown"
                filterMode={'select'}
                selectProps={{
                  variant: 'underlined',
                  allowClear: false,
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={intl.formatMessage({
                id: 'pages.leadsources.assignedTo',
                defaultMessage: 'Assign to',
              })}
              name="assignee_id"
            >
              <LeadAssigneeDropdownFilters
                type="dropdown"
                onlyAssignable
                hideDisabled
                defaultMe
                filterMode={'select'}
                selectProps={{
                  variant: 'underlined',
                  allowClear: true,
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <ProForm.Item
              label={
                <F
                  id="entity.product_group.singular"
                  defaultMessage="Product Group"
                />
              }
              name="product_group_id"
            >
              <ProductGroupSelect
                filterMode={'select'}
                hasCreate
                selectProps={{
                  variant: 'underlined',
                }}
              />
            </ProForm.Item>
          </Col>
          <Col span={12}>
            <ProFormDigit
              label={intl.formatMessage({
                id: 'pages.lead.fields.dealSize',
                defaultMessage: 'Deal Size',
              })}
              name="order_volume"
            />
          </Col>
        </Row>

        {/* Project Extra Fields */}
        <ExtraFields relatedTo="result" onValuesChange={onProjectExtraFieldValuesChange} />
      </ProForm>
    </InfoCard>
  );
};

export default ProjectForm;
